import { EnvironmentInjector, inject, isDevMode, runInInjectionContext } from '@angular/core';
import { getToken, MessagePayload, Messaging, NextFn, onMessage } from '@angular/fire/messaging';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, PushNotificationSchema, Token } from '@capacitor/push-notifications';
import { ApiService } from '@libs/core/base-config';
import { asResult } from '@libs/core/lib/state/async-states';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { addFcmToken, removeFcmToken } from '@libs/mtjr/shared/generated/entities/user/service/user.service';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';

export const NotificationsStore = signalStore(
  { providedIn: 'root' },
  withState<{
    failedToRequest: boolean;
    isRequesting: boolean;
    processedMessageIds: Set<string>;
    isNotificationSupported: boolean;
    isCapacitor: boolean;
  }>({
    failedToRequest: false,
    isRequesting: false,
    processedMessageIds: new Set<string>(),
    isNotificationSupported: 'Notification' in window && typeof Notification === 'function',
    isCapacitor: Capacitor.isNativePlatform(),
  }),

  withMethods(
    (
      store,
      messaging = inject(Messaging, { optional: true }) as Messaging | null,
      environmentInjector = inject(EnvironmentInjector),
      apiService = inject(ApiService),
    ) => ({
      async sendFcmTokenIfNeeded() {
        if (store.isCapacitor()) {
          // Use Capacitor push notifications
          try {
            const result = await PushNotifications.requestPermissions();
            if (result.receive !== 'granted') {
              console.warn('Push notification permission not granted');
              return;
            }

            // Register and wait for the token through the listener
            await PushNotifications.register();
          } catch (err) {
            console.error('Error in FCM token handling (Capacitor):', err);
            patchState(store, { failedToRequest: true });
          }
        } else {
          // Use web Firebase messaging
          if (!messaging) {
            console.error('Firebase Messaging not available');
            patchState(store, { failedToRequest: true });
            return;
          }

          await runInInjectionContext(environmentInjector, async () => {
            try {
              const fcmToken = await getToken(messaging);
              let tokenArray = [];
              const fcmTokensStr = localStorage.getItem('fcmTokens');
              if (fcmTokensStr) {
                tokenArray = JSON.parse(fcmTokensStr);
              }
              if (!tokenArray.includes(fcmToken)) {
                const result = await asResult(addFcmToken(apiService, { token: fcmToken }));
                if (result.success) {
                  tokenArray.push(fcmToken);
                  localStorage.setItem('fcmTokens', JSON.stringify(tokenArray));
                }
              }
            } catch (err) {
              console.error('Error in FCM token handling:', err);
              patchState(store, { failedToRequest: true });
            }
          });
        }
      },
    }),
  ),

  withMethods(
    (
      store,
      messaging = inject(Messaging, { optional: true }) as Messaging | null,
      environmentInjector = inject(EnvironmentInjector),
      apiService = inject(ApiService),
      alerts = inject(AppAlerts),
    ) => ({
      isGranted() {
        if (!store.isNotificationSupported()) {
          return false;
        }
        return Notification.permission === 'granted';
      },

      registerFirebaseMessaging() {
        if (!store.isNotificationSupported()) {
          console.warn('Notifications are not supported in this browser.');
          return;
        }

        if (store.isCapacitor()) {
          // Initialize Capacitor push notifications
          PushNotifications.addListener('registration', (token: Token) => {
            if (isDevMode()) console.log('Push registration success, token: ' + token.value);
            localStorage.setItem('capacitorFcmToken', token.value);

            // Send token to backend after registration
            void (async () => {
              try {
                let tokenArray = [];
                const fcmTokensStr = localStorage.getItem('fcmTokens');
                if (fcmTokensStr) {
                  tokenArray = JSON.parse(fcmTokensStr);
                }
                if (!tokenArray.includes(token.value)) {
                  const addResult = await asResult(addFcmToken(apiService, { token: token.value }));
                  if (addResult.success) {
                    tokenArray.push(token.value);
                    localStorage.setItem('fcmTokens', JSON.stringify(tokenArray));
                  }
                }
              } catch (err) {
                console.error('Error sending FCM token to backend:', err);
              }
            })();
          });

          PushNotifications.addListener('registrationError', (error: { error: string }) => {
            if (isDevMode()) console.error('Error on registration: ' + JSON.stringify(error));
          });

          PushNotifications.addListener('pushNotificationReceived', (notification) => {
            if (isDevMode()) console.log('Push notification received: ', notification);
          });
        } else {
          // Use web Firebase messaging
          if ('serviceWorker' in navigator) {
            navigator.serviceWorker
              .register('/firebase-messaging-sw.js', {
                scope: '/firebase-cloud-messaging-push-scope',
                updateViaCache: 'none',
              })
              .then(() => {
                if (isDevMode()) console.log('Firebase Messaging Service Worker registered');
              })
              .catch((error) => {
                if (isDevMode()) console.error('Firebase Messaging Service Worker registration failed:', error);
              });
          }
        }
      },

      async requestPermission(ops: { showAlert?: boolean; trMessage?: string }) {
        if (!store.isNotificationSupported()) {
          console.warn('Notifications are not supported in this browser.');
          return;
        }

        if (store.isCapacitor()) {
          // Handle Capacitor permissions
          patchState(store, { isRequesting: true });

          if (ops?.showAlert) {
            const confirmed = await alerts.yesNoAlertTR({
              header: 'NOTIFICATIONS.REQUEST_NOTIFICATION_PERMISSION_HEADER',
              message: ops.trMessage ?? 'NOTIFICATIONS.REQUEST_NOTIFICATION_PERMISSION_MESSAGE',
              yesCaption: 'NOTIFICATIONS.REQUEST_PERMISSION',
              noCaption: 'NOTIFICATIONS.REQUEST_LATER',
            });
            if (!confirmed) {
              patchState(store, { isRequesting: false });
              return;
            }
          }

          try {
            const permission = await PushNotifications.requestPermissions();
            patchState(store, { failedToRequest: false, isRequesting: false });

            if (permission.receive === 'denied') {
              alerts.dangerSnackbarTR('NOTIFICATIONS.NOTIFICATION_PERMISSION_DENIED', {
                position: 'top',
                duration: 3000,
              });
              return;
            }

            if (permission.receive === 'granted') {
              console.log('Permission granted');
              await store.sendFcmTokenIfNeeded();
            }
          } catch (err) {
            console.error('Error requesting notification permission (Capacitor):', err);
            patchState(store, { failedToRequest: true, isRequesting: false });
          }
        } else {
          // Handle web permissions
          if (Notification.permission === 'granted') {
            await store.sendFcmTokenIfNeeded();
            return;
          }
          patchState(store, { isRequesting: true });
          if (ops?.showAlert) {
            const confirmed = await alerts.yesNoAlertTR({
              header: 'NOTIFICATIONS.REQUEST_NOTIFICATION_PERMISSION_HEADER',
              message: ops.trMessage ?? 'NOTIFICATIONS.REQUEST_NOTIFICATION_PERMISSION_MESSAGE',
              yesCaption: 'NOTIFICATIONS.REQUEST_PERMISSION',
              noCaption: 'NOTIFICATIONS.REQUEST_LATER',
            });
            if (!confirmed) {
              return;
            }
          }

          try {
            const permission = await Notification.requestPermission();
            patchState(store, { failedToRequest: false, isRequesting: false });

            if (permission === 'denied') {
              alerts.dangerSnackbarTR('NOTIFICATIONS.NOTIFICATION_PERMISSION_DENIED', {
                position: 'top',
                duration: 3000,
              });
              return;
            }

            if (permission === 'granted') {
              console.log('Permission granted');

              if (!messaging) {
                console.error('Firebase Messaging not available');
                patchState(store, { failedToRequest: true });
                return;
              }

              await runInInjectionContext(environmentInjector, async () => {
                try {
                  const fcmToken = await getToken(messaging);
                  let tokenArray = [];
                  const fcmTokensStr = localStorage.getItem('fcmTokens');
                  if (fcmTokensStr) {
                    tokenArray = JSON.parse(fcmTokensStr);
                  }
                  if (!tokenArray.includes(fcmToken)) {
                    const result = await asResult(addFcmToken(apiService, { token: fcmToken }));
                    if (result.success) {
                      tokenArray.push(fcmToken);
                      localStorage.setItem('fcmTokens', JSON.stringify(tokenArray));
                    }
                  }
                } catch (err) {
                  console.error('Error in FCM token handling:', err);
                  patchState(store, { failedToRequest: true });
                }
              });
            }
          } catch (err) {
            console.error('Error requesting notification permission:', err);
            patchState(store, { failedToRequest: true, isRequesting: false });
          }
        }
      },

      async removeFcmToken(): Promise<boolean> {
        if (store.isCapacitor()) {
          // Handle Capacitor token removal
          try {
            // Note: Capacitor doesn't provide a direct way to get the current token
            // You might need to store it when registering
            const token = localStorage.getItem('capacitorFcmToken');
            if (token) {
              const result = await asResult(removeFcmToken(apiService, { token }));
              if (result.success) {
                const fcmTokensString = localStorage.getItem('fcmTokens') ?? '[]';
                const fcmTokens = JSON.parse(fcmTokensString);
                const index = fcmTokens.indexOf(token);
                if (index > -1) {
                  fcmTokens.splice(index, 1);
                }
                localStorage.setItem('fcmTokens', JSON.stringify(fcmTokens));
                localStorage.removeItem('capacitorFcmToken');
              }
              return result.success;
            }
            return false;
          } catch (error) {
            console.error('Error removing FCM token (Capacitor):', error);
            return false;
          }
        } else {
          // Handle web token removal
          if (!messaging) {
            console.error('Firebase Messaging not available');
            return false;
          }

          return runInInjectionContext(environmentInjector, async () => {
            const token = await getToken(messaging);
            const result = await asResult(removeFcmToken(apiService, { token }));
            if (result.success) {
              const fcmTokensString = localStorage.getItem('fcmTokens') ?? '[]';
              const fcmTokens = JSON.parse(fcmTokensString);
              const index = fcmTokens.indexOf(token);
              if (index > -1) {
                fcmTokens.splice(index, 1);
              }
              localStorage.setItem('fcmTokens', JSON.stringify(fcmTokens));
            }
            return result.success;
          });
        }
      },

      onMessage(callback: NextFn<MessagePayload>) {
        if (store.isCapacitor()) {
          // Handle Capacitor push notifications
          PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
            // Convert Capacitor notification to Firebase MessagePayload format
            const payload: MessagePayload = {
              from: notification.data?.from || '',
              collapseKey: notification.data?.collapseKey,
              notification: {
                title: notification.title,
                body: notification.body,
                icon: notification.data?.icon,
                image: notification.data?.image,
              },
              data: notification.data || {},
              messageId: notification.data?.messageId || notification.data?.fcmMessageId,
            };

            if (payload.messageId) {
              const processedMessageIds = store.processedMessageIds();
              if (processedMessageIds.has(payload.messageId)) return;
              patchState(store, { processedMessageIds: new Set([...processedMessageIds, payload.messageId]) });
              callback(payload);
            }
          });
        } else {
          // Handle web Firebase messaging
          if (!messaging) {
            console.error('Firebase Messaging not available');
            return;
          }

          try {
            runInInjectionContext(environmentInjector, () => {
              onMessage(messaging, (payload) => {
                // console.log({ payload });
                if (payload.messageId) {
                  const processedMessageIds = store.processedMessageIds();
                  if (processedMessageIds.has(payload.messageId)) return;
                  patchState(store, { processedMessageIds: new Set([...processedMessageIds, payload.messageId]) });
                  callback(payload);
                }
              });
            });
          } catch (error) {
            console.error(error);
          }
        }
      },
      onBackgroundMessage(callback: NextFn<MessagePayload>) {
        if (store.isCapacitor()) {
          // Handle Capacitor background notifications
          PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
            const payload: MessagePayload = {
              from: notification.notification.data?.from || '',
              collapseKey: notification.notification.data?.collapseKey,
              notification: {
                title: notification.notification.title,
                body: notification.notification.body,
              },
              data: notification.notification.data || {},
              messageId: notification.notification.data?.messageId || notification.notification.data?.fcmMessageId,
            };

            if (payload.messageId) {
              const processedMessageIds = store.processedMessageIds();
              if (processedMessageIds.has(payload.messageId)) return;
              patchState(store, { processedMessageIds: new Set([...processedMessageIds, payload.messageId]) });
              callback(payload);
            }
          });
        } else {
          // Handle web background messages
          navigator.serviceWorker.addEventListener('message', (event) => {
            setTimeout(() => {
              const data = event.data;
              // console.log({ data });
              const processedMessageIds = store.processedMessageIds();
              if (data.isFirebaseMessaging) {
                const fcmMessageId = data.fcmMessageId || data.messageId;
                if (fcmMessageId) {
                  if (processedMessageIds.has(fcmMessageId)) return;
                  patchState(store, { processedMessageIds: new Set([...processedMessageIds, fcmMessageId]) });
                  callback(data);
                }
              }
            }, 10);
          });
        }
      },
    }),
  ),

  withHooks(() => ({
    onInit: async () => {
      // if (store.isNotificationSupported()) {
      //   await store.sendFcmTokenIfNeeded();
      // }
    },
  })),
);
